import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { MainStore } from 'src/app/common/main.store';
import { LogSeverity, QStatus } from '../../synchronization.model';
import { QueueStatusClassPipe } from '../../pipes/queue-status-class.pipe';
import { QueueStatusLabelPipe } from '../../pipes/queue-status-label.pipe';
import { FormatRuntimePipe } from '../../pipes/format-runtime.pipe';
import { QueueExecuterDto, QueueLogEntryDto, QueueRequestDetailDto } from '../queue-request.model';
import { durationBetween, formatDurationMs, isExecutorSettled, phaseSpans } from '../queue-request.util';
import { ExecutorAccordionComponent } from '../executor-accordion/executor-accordion.component';
import { QueueLogRowComponent } from '../queue-log-row/queue-log-row.component';
import { TaskBadgeComponent } from '../task-badge/task-badge.component';

/** The origin StatusService logs its rollup under — the one marker that separates start from finish. */
const ROLLUP_ORIGIN = 'Queue Status';

/**
 * One stop on the kørsel-level timeline. `status` renders through queueStatusLabel; the two synthetic
 * stops (the chunk block and the rollup) carry a `label` instead.
 */
export interface TimelineItem {
    kind: 'phase' | 'chunks' | 'end';
    status: QStatus | null;
    label: string | null;
    start: string;
    durationLabel: string;
}

/**
 * The right-hand pane: one kørsel's header, its kørsel-level log spine and its chunk accordion.
 *
 * One `request` input rather than a header from the list row plus a body from a resource — that
 * combination renders one kørsel's logs under another's header while a fetch is in flight.
 *
 * Two tiers of information. Everyone sees start, end, status and errors. Admins (developers) also see
 * the timing detail: the kørsel-level timeline, per-chunk phase durations, and Trace-severity log
 * lines. A user can tell "it is slow"; only a developer needs to see where the time went.
 */
@Component({
    selector: 'app-queue-request-detail',
    templateUrl: './queue-request-detail.component.html',
    styleUrl: './queue-request-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        RouterLink,
        NgbTooltip,
        QueueStatusClassPipe,
        QueueStatusLabelPipe,
        FormatRuntimePipe,
        ExecutorAccordionComponent,
        QueueLogRowComponent,
        TaskBadgeComponent
    ]
})
export class QueueRequestDetailComponent {
    readonly request = input<QueueRequestDetailDto | null>(null);
    readonly customerName = input('');
    readonly companyName = input('');
    readonly synchronizationName = input('');
    readonly loading = input(false);
    readonly error = input<string | null>(null);

    readonly downloadPayload = output<QueueExecuterDto>();
    readonly copyLink = output<void>();

    /** Developer detail is gated the same way as everywhere else in the Configurator. */
    protected readonly isAdmin = inject(MainStore).currentUser.get().isAdmin;

    protected readonly headerExpanded = signal(true);
    protected readonly skeletonRows = [0, 1, 2, 3, 4];

    protected readonly progress = computed(() => {
        const executers = this.request()?.executers ?? [];
        if (executers.length === 0) return null;

        // Færdig means stopped, not succeeded — a fejlet delkørsel is finished too.
        const done = executers.filter(e => isExecutorSettled(e.status)).length;
        const failed = executers.filter(e => e.status === QStatus.Failed).length;
        return {
            done,
            failed,
            total: executers.length,
            percent: Math.round((done / executers.length) * 100)
        };
    });

    /**
     * The log lines this user gets to see. Trace and Debug rows are the synchronization tool's
     * developer commentary (per-chunk timings and the like) and stay hidden from non-admins; a user
     * cares about start, end and errors.
     */
    protected readonly visibleLogs = computed<readonly QueueLogEntryDto[]>(() => {
        const logs = this.request()?.logs ?? [];
        return this.isAdmin
            ? logs
            : logs.filter(entry => entry.severity !== LogSeverity.Trace && entry.severity !== LogSeverity.Debug);
    });

    /**
     * Kørsel-level lines written before the chunks — request accepted, validation, routing.
     *
     * Split from the trailing group by origin: StatusService logs its rollup under "Queue Status" and
     * nothing else does, so it is the one marker available for "after the chunks finished". Chunk
     * lines are excluded here entirely — the accordion renders them under their own delkørsel.
     */
    protected readonly leadingLogs = computed(() =>
        this.visibleLogs().filter(entry => !entry.executerId && entry.origin !== ROLLUP_ORIGIN)
    );

    protected readonly trailingLogs = computed(() =>
        this.visibleLogs().filter(entry => !entry.executerId && entry.origin === ROLLUP_ORIGIN)
    );

    /**
     * The kørsel's life as stops on a line: the request-level tracker phases (Venter, Validerer, …),
     * then the delkørsler as one block spanning first pickup to last completion, then the rollup.
     *
     * The last request-level phase lasts until the first chunk starts — that gap is the Validate →
     * Synchronize hop, which is exactly the kind of number a developer opens this pane for. A legacy
     * kørsel (chunk rows with random sender ids) has no chunk block; the server has already collapsed
     * its chunk rows into Executing/Done stops on the timeline itself.
     */
    protected readonly timeline = computed<TimelineItem[]>(() => {
        const detail = this.request();
        if (!detail || !this.isAdmin) return [];

        const started = detail.executers
            .map(e => e.startedAt)
            .filter((start): start is string => !!start)
            .sort();
        const completed = detail.executers
            .map(e => e.completedAt)
            .filter((end): end is string => !!end)
            .sort();

        const chunkStart = started[0] ?? null;
        const chunkEnd = detail.completedAt ?? completed[completed.length - 1] ?? null;
        const allSettled = detail.executers.length > 0 && detail.executers.every(e => isExecutorSettled(e.status));

        const items: TimelineItem[] = phaseSpans(detail.timeline, chunkStart ?? chunkEnd).map(span => ({
            kind: 'phase',
            status: span.status,
            label: null,
            start: span.start,
            durationLabel: span.durationLabel
        }));

        if (chunkStart) {
            const chunkDuration = chunkEnd ? durationBetween(chunkStart, chunkEnd) : null;
            items.push({
                kind: 'chunks',
                status: null,
                label: detail.executers.length === 1 ? '1 delkørsel' : `${detail.executers.length} delkørsler`,
                start: chunkStart,
                durationLabel: chunkDuration !== null ? formatDurationMs(chunkDuration) : allSettled ? '' : '…'
            });
        }

        if (detail.completedAt) {
            items.push({ kind: 'end', status: null, label: 'Afsluttet', start: detail.completedAt, durationLabel: '' });
        }

        return items;
    });

    toggleHeader(): void {
        this.headerExpanded.update(expanded => !expanded);
    }
}
